import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';

/**
 * A text input that reports its value once the user stops typing.
 *
 * ⚠ IT ALSO HAS TO FOLLOW THE ROW BEHIND IT. `defaultValue` on an uncontrolled input applies at
 * MOUNT and never again, so a cell whose value arrives later — a bulk Classify writing three fields
 * across seven rows, another tab, any refetch — kept showing whatever it mounted with. Measured on
 * dev: after a bulk Classify the API held the new value on every row while the cells displayed an
 * empty string, and the same defect showed as STALE values on staging where the cells had mounted
 * with something in them. It is the reason RG7 reads as "the fields are not updated" even once the
 * writes are correct: the data is right and the table is not showing it.
 *
 * The dropdown cell never had this because `getTableSelect` renders its Select with
 * `key={defaultValue}`, which remounts it whenever the value changes.
 *
 * ⚠ SO WHY NOT JUST KEY THIS THE SAME WAY? Because it would re-introduce H1MVP-4807. The debounce is
 * 2000ms, so a user who resumes typing after it fires is still editing when the earlier save's
 * success merges into the row — a remount at that moment would replace what they are typing with
 * the older saved value. Syncing in an effect and SKIPPING WHILE THE USER OWNS THE FIELD keeps both
 * properties: the cell follows the row, and it never overwrites an edit in progress.
 *
 * "Owns the field" is focus OR a pending debounce, deliberately. Focus alone is not enough — a user
 * can type and click away inside the 2s window, leaving an unsent edit in a blurred input.
 *
 * Pass `value` to drive it as a normal controlled input; `value` always wins.
 */
const DebouncedInput = ({
  value,
  defaultValue,
  onChange,
  onDebouncedChange,
  onFocus,
  onBlur,
  debounceTime = 3000,
  ...rest
}) => {
  const timerRef = useRef(null);
  const focusedRef = useRef(false);
  const [innerValue, setInnerValue] = useState(defaultValue ?? '');

  // Follow the row, unless the user is in the middle of an edit.
  useEffect(() => {
    if (focusedRef.current || timerRef.current) return;
    setInnerValue(defaultValue ?? '');
  }, [defaultValue]);

  // The timer outlived the component: a cell unmounted by a refetch still fired its callback,
  // writing a value from a row that was no longer on screen.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInnerValue(newValue);

    if (onChange) {
      onChange(newValue);
    }

    // Clear previous debounce timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new debounce timer
    timerRef.current = setTimeout(() => {
      // Cleared BEFORE the callback: the effect above treats a live timer as "the user is still
      // editing", and leaving it set would block the sync of the very value being saved.
      timerRef.current = null;
      if (onDebouncedChange) {
        onDebouncedChange(newValue);
      }
    }, debounceTime);
  };

  const handleFocus = (e) => {
    focusedRef.current = true;
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    focusedRef.current = false;
    onBlur?.(e);
  };

  return (
    <Input
      {...rest}
      value={value === undefined ? innerValue : value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export default DebouncedInput;
